/* verqo — Buyer Product page content (13 pages)
   Same catalog pattern as seller: hero visual + rich features + screenshot showcase.
   Screenshots captured from the LIVE buyer portal (buyer@apex.com — Apex Wholesale LLC).
   No demo CTAs.
*/
import {
  Search, Layers3, MessageSquare, RotateCw,
  Landmark, TrendingUp, PieChart, LayoutDashboard,
  Wallet, Zap, Banknote, Split,
  Truck, Shield, Sparkles, Clock, ShieldCheck, Layers,
  Building2, DollarSign, Percent, Bell, Bot, Gauge, ChartBar,
  CheckCircle2, LinkIcon, RefreshCw, Lock, Users2,
} from "lucide-react";
import { BrowserFrame } from "../../../../components/marketing/SubPageBlocks";

const CTA_PRIMARY = { label: "Request invite", href: "/waitlist?as=buyer" };

const SHOWCASE_CTA = {
  primary:   { label: "Request invite",   href: "/waitlist?as=buyer" },
  secondary: { label: "See how it works", href: "/company/how-it-works" },
};

const commonCta = {
  title: "One login. Every supplier. Net-30 in seconds.",
  subtitle: "Verqo Buyer is invite-only during our launch phase. Join the list and we'll get you set up in under 48 hours.",
  primary: CTA_PRIMARY,
  secondary: { label: "Talk to us", href: "/company/contact" },
};

const REL = (slug, label, desc) => ({ href: `/buyers/product/${slug}`, label, desc });

const IMG = {
  dashboard:      "/preview-screens/buyer-dashboard/main.jpeg",
  ordersAll:      "/preview-screens/buyer-orders/all.jpeg",
  ordersAction:   "/preview-screens/buyer-orders/action.jpeg",
  ordersJourney:  "/preview-screens/buyer-orders/journey.jpeg",
  paymentsOverview:"/preview-screens/buyer-payments/overview.jpeg",
  paymentsWallet: "/preview-screens/buyer-payments/wallet.jpeg",
  paymentsCredit: "/preview-screens/buyer-payments/credit.jpeg",
  paymentsPreCredit:"/preview-screens/buyer-payments/precredit.jpeg",
  paymentsMethods:"/preview-screens/buyer-payments/methods.jpeg",
  invoices:       "/preview-screens/buyer-invoices/main.jpeg",
  score:          "/preview-screens/buyer-score/main.jpeg",
  shipping:       "/preview-screens/buyer-shipping/main.jpeg",
  suppliers:      "/preview-screens/buyer-suppliers/main.jpeg",
  poInbox:        "/preview-screens/buyer-po-inbox/main.jpeg",
  financing:      "/preview-screens/buyer-financing/main.jpeg",
};

const URL = (path) => `app.verqo.com${path}`;

/* -------- 1. DISCOVER -------- */
const discover = {
  eyebrow: "Buyers · Discover",
  title: "One search. Every wholesale supplier that matters.",
  subtitle: "Instead of vetting 40 vendors on Instagram DMs, discover suppliers whose catalog, terms, and MOQs actually fit your store — all in one place, all verified, all instantly orderable.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.suppliers} alt="Verqo Discover — supplier directory" url={URL("/buyer/suppliers")} />,
  features: {
    heading: "The wholesale discovery engine you always wanted.",
    items: [
      { icon: Search,        title: "Filter by everything",   desc: "MOQ, price tier, region, terms, lead time — all filterable in real time." },
      { icon: Sparkles,      title: "Curated shortlists",      desc: "AI shortlists based on what your store already carries." },
      { icon: TrendingUp,    title: "Verified trade signal",   desc: "See how each supplier ships, communicates, and settles — from real data." },
      { icon: ShieldCheck,   title: "Approved fast",           desc: "Instant approval with 90%+ of Verqo suppliers on our network." },
      { icon: Building2,     title: "Verified suppliers",       desc: "Every supplier KYB'd, tax-verified, and quality-graded before appearing in your search." },
      { icon: LinkIcon,      title: "Cross-supplier cart",      desc: "Add SKUs from any supplier to one cart — Verqo handles the split-PO and payment routing." },
    ],
  },
  blocks: [
    {
      eyebrow: "Not a marketplace",
      title: "You're not paying a 25% commission to a middleman.",
      body: "Verqo doesn't take a cut of your GMV. No commission, no markup on your orders — just transparent usage-based fees for the services you choose to use.",
      visual: <BrowserFrame src={IMG.dashboard} alt="Buyer hub — connected suppliers dashboard" url={URL("/buyer")} />,
    },
  ],
  related: [
    REL("multi-supplier","Multi-supplier","One cart, many brands."),
    REL("credit-terms",  "Credit terms",  "Net-30/60/90 in one click."),
    REL("trade-score",   "Trade score",    "Your buy-side reputation, portable."),
  ],
  cta: commonCta,
};

/* -------- 2. MULTI-SUPPLIER -------- */
const multiSupplier = {
  eyebrow: "Buyers · Discover",
  title: "One cart. Many brands. One PO.",
  subtitle: "Add SKUs from any Verqo supplier to a single cart. Verqo handles the split, the POs, and the payment routing — you write one check.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.dashboard} alt="Buyer Hub — all suppliers in one dashboard" url={URL("/buyer")} />,
  features: {
    heading: "Buy like it's a marketplace. Reconcile like it's your ERP.",
    items: [
      { icon: Layers3,         title: "Split cart, split PO", desc: "One cart auto-splits into per-supplier POs on checkout — each supplier sees their line items only." },
      { icon: Wallet,          title: "One payment",          desc: "Pay Verqo. Verqo pays every supplier on the terms you set. One bank draw, forty settlements." },
      { icon: Truck,           title: "Consolidated freight", desc: "Combine parcels when it makes sense, split when it doesn't — Verqo picks per shipment." },
      { icon: LayoutDashboard, title: "One ledger",           desc: "Every open PO, every invoice, every payment in one view — across every supplier." },
      { icon: Bot,             title: "AI reorder cadence",   desc: "AI learns your buying patterns and suggests reorder timing per supplier." },
      { icon: Users2,          title: "Multi-user approval",  desc: "For chains and marketplaces — approval workflows per PO value or category." },
    ],
  },
  screenshots: {
    heading: "PO Inbox — every open PO across every supplier.",
    sub: "One universal inbox. Send POs to suppliers on Verqo, or even to suppliers NOT on Verqo — they still receive a branded PO link.",
    items: [
      { label: "PO Inbox",  src: IMG.poInbox,   alt: "PO Inbox — 230 POs across every supplier",  url: URL("/buyer/po-inbox"),  caption: "230 POs, $194K value, across 40+ suppliers — some on Verqo, some off. Send, track, and settle from one dashboard." },
      { label: "Orders",    src: IMG.ordersAll, alt: "Orders — cross-supplier action queue",       url: URL("/buyer/orders"),    caption: "Cross-supplier order queue — 919 open orders across two connected brands. Pay, book carrier, or dispute in one click." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "One AP process, not fifty",
      title: "Cut AP work by 90%.",
      body: "Your bookkeeper used to reconcile 40 supplier invoices a month. Now they reconcile one Verqo statement. Same audit trail, less pain.",
      visual: <BrowserFrame src={IMG.paymentsOverview} alt="Buyer Payments overview — money in/out, wallet, credit" url={URL("/buyer/payments")} />,
    },
  ],
  related: [
    REL("discover",  "Discover",  "Find new suppliers to add to the cart."),
    REL("pay",       "Pay",       "How the money moves under the hood."),
    REL("credit-dashboard","Credit dashboard","Track every open invoice."),
  ],
  cta: commonCta,
};

/* -------- 3. RFQ -------- */
const rfq = {
  eyebrow: "Buyers · Discover",
  title: "One RFQ. Parallel bids from every supplier who fits.",
  subtitle: "Post what you need — Verqo routes it to relevant suppliers and returns priced, term-attached bids in 24-48 hours. No more emailing 15 vendors and comparing spreadsheets.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.poInbox} alt="Buyer PO Inbox — RFQ + PO flow" url={URL("/buyer/po-inbox")} />,
  features: {
    heading: "The RFQ tool wholesale should have had years ago.",
    items: [
      { icon: MessageSquare,   title: "Structured request",  desc: "SKU list, target price, quantity, ship-to, and terms — one form for every RFQ." },
      { icon: Layers,          title: "Side-by-side bids",   desc: "Every response normalized so you can compare apples to apples — price, MOQ, lead time, payment terms." },
      { icon: Clock,           title: "SLA tracking",        desc: "See which suppliers responded on time. Which never bothered. Their score drops accordingly." },
      { icon: DollarSign,      title: "Terms in the bid",    desc: "Every bid includes payment terms — Net-30 / 60 / 90 or pay-now — up-front, not after negotiation." },
    ],
  },
  related: [
    REL("multi-supplier","Multi-supplier","Order across every supplier in one PO."),
    REL("discover",       "Discover",     "Find new suppliers for the next RFQ."),
    REL("pay",            "Pay",          "Settle the winning bid on the terms you like."),
  ],
  cta: commonCta,
};

/* -------- 4. REORDER -------- */
const reorder = {
  eyebrow: "Buyers · Discover",
  title: "Never run out of your best-sellers again.",
  subtitle: "Set a reorder rule per SKU — days on hand, weekly velocity, or seasonal pattern — and Verqo triggers the PO automatically. You approve, we ship.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.ordersAction} alt="Buyer Orders — action queue with pending reorders" url={URL("/buyer/orders#action")} />,
  features: {
    heading: "Autopilot on your top 20% of SKUs.",
    items: [
      { icon: RotateCw,   title: "Rules-based reorder", desc: "Days-on-hand, min-max, or fixed cadence — you pick." },
      { icon: Layers,     title: "Multi-supplier bundles",desc: "Batch reorders across suppliers to hit MOQs and save on freight." },
      { icon: Zap,        title: "One-tap approve",      desc: "Every triggered PO lands in your approval queue, ready to send." },
      { icon: Bell,       title: "Low-stock alerts",     desc: "Slack, email, or SMS when a top-seller drops below reorder threshold." },
      { icon: Bot,        title: "AI velocity forecast", desc: "AI predicts sell-through based on last 90 days and seasonal signal." },
    ],
  },
  screenshots: {
    heading: "Every open order, one view.",
    sub: "Journey, action, in-progress, delivered — every stage from PO submission to receiving in a single cross-supplier queue.",
    items: [
      { label: "All orders",    src: IMG.ordersAll,     alt: "Buyer Orders — 919 open orders",           url: URL("/buyer/orders"),           caption: "919 open orders across every connected supplier — filter by supplier, SKU, or status." },
      { label: "Action queue",  src: IMG.ordersAction,  alt: "Buyer Orders — action queue",              url: URL("/buyer/orders#action"),    caption: "Action needed — 634 orders awaiting your pay, receive, or dispute. Sorted by SLA proximity." },
      { label: "Journey",       src: IMG.ordersJourney, alt: "Buyer Orders — journey (onboarding)",      url: URL("/buyer/orders#journey"),   caption: "Journey — buyer-side onboarding walk-through showing new users how to place their first order." },
    ],
    cta: SHOWCASE_CTA,
  },
  related: [
    REL("discover", "Discover", "For net-new SKUs."),
    REL("multi-supplier","Multi-supplier","One cart across every brand."),
    REL("auto-debit","Auto-debit","Never miss a payment on a reorder."),
  ],
  cta: commonCta,
};

/* -------- 5. CREDIT TERMS (buyer) -------- */
const creditTerms = {
  eyebrow: "Buyers · Get terms",
  title: "Net-30/60/90 across every supplier — instantly.",
  subtitle: "Get pre-approved once. Use your Verqo line at any supplier on the network. No more re-applying, no more credit apps, no more waiting on Net-30 approvals.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.paymentsCredit} alt="Buyer Credit — line, utilization, next repayment" url={URL("/buyer/payments#credit")} />,
  features: {
    heading: "A credit line that follows you everywhere.",
    items: [
      { icon: Landmark,      title: "One pre-approval",     desc: "One application unlocks every supplier on the network — no more resubmitting your financials to each brand." },
      { icon: TrendingUp,    title: "Line grows with you",  desc: "Pay on time, and your limit auto-increases every 90 days — up to $500K for approved retailers." },
      { icon: ShieldCheck,   title: "Non-recourse to supplier",desc: "Suppliers get paid day 1. They love it. You get real terms without begging." },
      { icon: Clock,         title: "Instant checkout",     desc: "Pick Net-30/60/90 at checkout — approved in under 30 seconds." },
      { icon: Percent,       title: "Transparent rate",     desc: "See the exact cost before you commit — usually 0% for base terms, 1-3% for extended." },
    ],
  },
  screenshots: {
    heading: "Every credit event, one dashboard.",
    sub: "Pre-credit onboarding, active credit line, aging invoices — the whole flow.",
    items: [
      { label: "Credit line",  src: IMG.paymentsCredit,    alt: "Buyer Credit line — utilization",         url: URL("/buyer/payments#credit"),    caption: "Live credit line dashboard — total limit, utilization, and next repayment schedule per supplier." },
      { label: "Pre-credit",   src: IMG.paymentsPreCredit, alt: "Pre-credit onboarding",                    url: URL("/buyer/payments#pre-credit"),caption: "Pre-credit — the onboarding flow to unlock your line. KYB, bank connect, and instant underwriting." },
      { label: "Invoices",     src: IMG.invoices,          alt: "Buyer Invoices — every open invoice",      url: URL("/buyer/invoices"),          caption: "Every open invoice across every supplier — with due dates, extend options, and pay-now buttons." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "The math",
      title: "You pay Net-30/60/90. Suppliers get paid day 1. Everyone wins.",
      body: "Verqo underwrites your line and covers the gap. Suppliers stop chasing checks; you stop begging for terms.",
      visual: <BrowserFrame src={IMG.paymentsOverview} alt="Buyer Payments overview — one view of every dollar" url={URL("/buyer/payments")} />,
    },
  ],
  related: [
    REL("trade-score",      "Trade score",      "How your reputation earns you a bigger line."),
    REL("credit-dashboard", "Credit dashboard", "Every open invoice in one view."),
    REL("financing",         "Financing",        "Extend even further when cash is tight."),
  ],
  cta: commonCta,
};

/* -------- 6. TRADE SCORE -------- */
const tradeScore = {
  eyebrow: "Buyers · Get terms",
  title: "A credit reputation that travels with you.",
  subtitle: "Every on-time payment across every Verqo supplier improves your Trade Score — which unlocks bigger credit lines, better terms, and preferred supplier tiers.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.score} alt="Buyer Trade Score — live 0-100 credit health" url={URL("/buyer/score")} />,
  features: {
    heading: "Your buy-side reputation, portable across the network.",
    items: [
      { icon: Gauge,         title: "Live 0-100 score",   desc: "Updates in real time based on payment behavior, open exposure, and dispute history." },
      { icon: TrendingUp,    title: "Unlocks better terms",desc: "Higher score → bigger lines, longer terms, preferred pricing tiers across the network." },
      { icon: Building2,     title: "Cross-supplier signal",desc: "Every supplier sees the same score. Consistent trust, everywhere you buy." },
      { icon: ChartBar,      title: "Score trend",         desc: "See what's moving your score up or down — velocity, disputes, aging — with actionable coaching." },
      { icon: Bell,          title: "Milestone alerts",    desc: "Get pinged when you hit a new tier (700+ Prime, 800+ Elite) with new unlocked perks." },
    ],
  },
  related: [
    REL("credit-terms",     "Credit terms",     "Terms unlocked by your score."),
    REL("credit-dashboard", "Credit dashboard", "Every open invoice in one view."),
    REL("financing",         "Financing",        "Access extended financing at strong scores."),
  ],
  cta: commonCta,
};

/* -------- 7. CREDIT DASHBOARD -------- */
const creditDashboard = {
  eyebrow: "Buyers · Get terms",
  title: "Every open invoice, every due date, one view.",
  subtitle: "Credit dashboard shows exactly how much you owe, to whom, and when — plus what happens if you pay early, split, or extend. AP that respects your time.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.paymentsOverview} alt="Buyer Credit Dashboard — money in/out, wallet, credit" url={URL("/buyer/payments")} />,
  features: {
    heading: "The AP dashboard your bookkeeper actually wants.",
    items: [
      { icon: PieChart,      title: "Aging buckets",       desc: "0-30, 31-60, 61-90 at a glance." },
      { icon: Wallet,        title: "Total exposure",      desc: "Your open credit + open POs + upcoming reorders — one number per supplier." },
      { icon: Zap,           title: "One-tap pay",         desc: "Pay a single invoice, batch, or your entire month with a click." },
      { icon: Percent,       title: "Discount opportunities",desc: "See which invoices offer early-pay discounts — grab them one-click." },
      { icon: Bell,          title: "Payment reminders",    desc: "Slack, email, or in-app alerts before terms mature." },
    ],
  },
  screenshots: {
    heading: "AP, six tabs, one login.",
    sub: "Overview / Wallet / Credit / Pre-credit / Invoices / Methods / Activity — every dollar movement across every supplier.",
    items: [
      { label: "Overview",   src: IMG.paymentsOverview, alt: "Overview — money in/out summary",  url: URL("/buyer/payments"),         caption: "Overview — total money-in, money-out, wallet balance, and credit utilization in a single glance." },
      { label: "Wallet",     src: IMG.paymentsWallet,   alt: "Wallet — top-up + transfer flow",   url: URL("/buyer/payments#wallet"),  caption: "Wallet — top up from your bank, transfer between suppliers, and see clearing status live." },
      { label: "Invoices",   src: IMG.invoices,         alt: "Invoices dashboard",                url: URL("/buyer/invoices"),         caption: "Every open invoice with pay-now, split, or extend options — filter by supplier or aging bucket." },
      { label: "Methods",    src: IMG.paymentsMethods,  alt: "Payment methods — bank & cards",    url: URL("/buyer/payments#methods"), caption: "Methods — Chase connected via Plaid + optional cards. Verified with $5.7M inflow history, 0 NSF events." },
    ],
    cta: SHOWCASE_CTA,
  },
  related: [
    REL("credit-terms","Credit terms","Terms across every supplier."),
    REL("auto-debit",  "Auto-debit",  "Never miss a due date."),
    REL("split-pay",    "Split pay",   "Split any invoice into 3 or 6 payments."),
  ],
  cta: commonCta,
};

/* -------- 8. PAY -------- */
const pay = {
  eyebrow: "Buyers · Pay your way",
  title: "One wallet. Every payment method. Every supplier.",
  subtitle: "ACH, card, wire, or credit line — Verqo Pay routes your payment on the rails you choose. Suppliers always get paid the same way, on time.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.paymentsWallet} alt="Buyer Wallet — balance, top-up, transfer" url={URL("/buyer/payments#wallet")} />,
  features: {
    heading: "Consumer-simple. Enterprise-grade.",
    items: [
      { icon: Wallet,      title: "Multi-rail wallet",  desc: "ACH, card, wire, and credit line — one interface." },
      { icon: Zap,         title: "Instant routing",     desc: "Pay from the cheapest rail that meets your rules — automatic per invoice." },
      { icon: TrendingUp,  title: "Earn on prepay",      desc: "Take an early-pay discount when you have the cash — 2/10 Net-30 auto-applied." },
      { icon: Lock,        title: "Bank-level security", desc: "Plaid-connected, MFA-required, SOC 2 Type II." },
    ],
  },
  screenshots: {
    heading: "The buyer wallet, top to bottom.",
    items: [
      { label: "Wallet",   src: IMG.paymentsWallet,  alt: "Wallet — $50K available",  url: URL("/buyer/payments#wallet"),  caption: "Live balance, one-click top-up from Chase (via Plaid), and instant transfer out to any connected supplier." },
      { label: "Methods",  src: IMG.paymentsMethods, alt: "Methods — Chase + Plaid",   url: URL("/buyer/payments#methods"), caption: "Methods — Chase connected via Plaid (sandbox), $5.7M lifetime inflow, 482 transactions, 0 NSF events." },
    ],
    cta: SHOWCASE_CTA,
  },
  related: [
    REL("auto-debit","Auto-debit","Set it and forget it."),
    REL("financing","Financing","Extend when cash is tight."),
    REL("credit-dashboard","Credit dashboard","See every open invoice."),
  ],
  cta: commonCta,
};

/* -------- 9. AUTO-DEBIT -------- */
const autoDebit = {
  eyebrow: "Buyers · Pay your way",
  title: "Never miss a due date. Never pay a late fee.",
  subtitle: "Set auto-debit rules per supplier — Verqo pulls from your linked account on the day terms are due. Your bookkeeper stops chasing bills; your suppliers stop chasing you.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.paymentsMethods} alt="Payment methods — auto-debit configured" url={URL("/buyer/payments#methods")} />,
  features: {
    heading: "AP autopilot without giving up control.",
    items: [
      { icon: Zap,          title: "Rules per supplier",  desc: "Auto-debit all, or only invoices below your threshold, or only from suppliers you've marked." },
      { icon: Clock,        title: "48h heads-up",         desc: "Preview the debit two days out — cancel any line with one tap." },
      { icon: ShieldCheck,  title: "Reversal insurance",   desc: "Dispute an auto-debit within 60 days if something's wrong." },
      { icon: RefreshCw,    title: "Retry on soft-decline",desc: "If your bank returns a soft-decline, Verqo retries automatically over 3 days." },
    ],
  },
  related: [
    REL("pay",              "Pay",              "Multi-rail wallet under the hood."),
    REL("credit-dashboard", "Credit dashboard", "See every upcoming debit."),
    REL("financing",         "Financing",        "Extend beyond terms when needed."),
  ],
  cta: commonCta,
};

/* -------- 10. FINANCING -------- */
const financing = {
  eyebrow: "Buyers · Pay your way",
  title: "Extend terms up to 120 days when cash is tight.",
  subtitle: "Approved buyers can push any invoice from Net-30 to Net-60/90/120 with one tap — priced transparently, no hidden fees, no relationship risk with your supplier.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.financing} alt="Invoice financing — extend terms" url={URL("/buyer/hub/financing")} />,
  features: {
    heading: "Working capital that respects your business.",
    items: [
      { icon: Banknote,      title: "Transparent rate",    desc: "See the exact cost before you extend — usually 1-3% flat, no APR games." },
      { icon: Wallet,        title: "No hidden fees",       desc: "No origination, no draw fees, no prepayment penalties." },
      { icon: TrendingUp,    title: "Limit auto-grows",     desc: "Pay on time and your extension limit grows with you." },
      { icon: Users2,        title: "Supplier stays clean", desc: "Suppliers get paid on their original terms — they never see the extension." },
      { icon: Bell,          title: "Auto-reminders",       desc: "48h before extended terms mature, we ping so you're never caught out." },
    ],
  },
  related: [
    REL("credit-terms",   "Credit terms",   "Your baseline terms across the network."),
    REL("split-pay",      "Split pay",       "Split into 3 or 6 monthly payments."),
    REL("trade-score",    "Trade score",     "How your score unlocks bigger financing."),
  ],
  cta: commonCta,
};

/* -------- 11. SPLIT PAY -------- */
const splitPay = {
  eyebrow: "Buyers · Pay your way",
  title: "Split any invoice into 3 or 6 monthly payments.",
  subtitle: "For seasonal buyers and long-tail items, split-pay lets you sell through inventory before paying it off. One click at checkout, transparent APR.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.financing} alt="Split pay — invoice into 3 or 6 installments" url={URL("/buyer/hub/financing")} />,
  features: {
    heading: "BNPL, built for wholesale.",
    items: [
      { icon: Split,         title: "3 or 6 splits",   desc: "Break any invoice into 3 or 6 equal monthly installments." },
      { icon: Banknote,      title: "Transparent APR",   desc: "See the exact APR before you commit — no gotchas." },
      { icon: TrendingUp,    title: "No credit hit",     desc: "Uses your existing Verqo line — no separate application, no new bureau pull." },
    ],
  },
  related: [
    REL("financing",   "Financing",   "For longer extensions."),
    REL("credit-terms","Credit terms","Baseline network-wide terms."),
    REL("auto-debit", "Auto-debit",  "Splits auto-debited on schedule."),
  ],
  cta: commonCta,
};

/* -------- 12. SHIP (buyer) -------- */
const ship = {
  eyebrow: "Buyers · Ship & protect",
  title: "Consolidated freight from every supplier.",
  subtitle: "When you buy from three suppliers, Verqo consolidates the parcels or freight when it's cheaper — and splits it when it's faster. Either way, one tracking view.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.shipping} alt="Buyer Shipping — LTL & FTL + parcel unified" url={URL("/buyer/shipping")} />,
  features: {
    heading: "Shipping that works across your suppliers, not around them.",
    items: [
      { icon: Truck,        title: "Consolidation",      desc: "Combine parcels or LTL from multiple suppliers when possible." },
      { icon: Layers,       title: "One tracking view",   desc: "Every inbound in one dashboard — no chasing tracking numbers." },
      { icon: Shield,       title: "Insured by default",  desc: "Every shipment covered — file claims in 30 seconds." },
      { icon: Bot,          title: "AI rate compare",     desc: "AI picks the cheapest rate meeting your SLA — parcel vs LTL trade-off automatic." },
    ],
  },
  screenshots: {
    heading: "Shipping — the whole flow.",
    sub: "43 delivered · 100% on-time · $20K freight spend · 4.2d avg transit — all live.",
    items: [
      { label: "Overview",     src: IMG.shipping, alt: "Shipping overview with LTL/FTL + parcel", url: URL("/buyer/shipping"), caption: "Overview — inbound freight metrics (in-transit, delivered, transit days, spend), LTL/FTL and parcel status blocks, and step-by-step book-shipment flow." },
    ],
    cta: SHOWCASE_CTA,
  },
  related: [
    REL("multi-supplier","Multi-supplier","One cart across every supplier."),
    REL("insurance",      "Insurance",     "Buyer-side cover on every parcel."),
    REL("credit-dashboard","Credit dashboard","See every open invoice."),
  ],
  cta: commonCta,
};

/* -------- 13. INSURANCE (buyer) -------- */
const insuranceBuyer = {
  eyebrow: "Buyers · Ship & protect",
  title: "Buyer-side cover on every dropship parcel.",
  subtitle: "Damaged inbound? Missing merchandise? Verqo Insurance covers the buyer side — so you never end up carrying the loss because a supplier and carrier are pointing fingers.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.shipping} alt="Buyer-side insurance embedded in every shipment" url={URL("/buyer/shipping")} />,
  features: {
    heading: "The safety net wholesale never had for buyers.",
    items: [
      { icon: Shield,        title: "Damage & loss cover", desc: "Up to $5K per parcel, Allianz-backed." },
      { icon: Clock,         title: "Fast claims",          desc: "File in 30 seconds. Paid to your Verqo wallet in days." },
      { icon: Wallet,        title: "Zero premium",         desc: "Cover embedded in every Verqo-routed shipment. You never see a bill." },
      { icon: ShieldCheck,   title: "Dispute defense",       desc: "Verqo's team handles the back-and-forth with the carrier and supplier — you keep receiving inventory." },
    ],
  },
  related: [
    REL("ship",   "Ship",   "How consolidated freight works."),
    REL("pay",    "Pay",    "Multi-rail wallet for every settlement."),
    REL("credit-dashboard","Credit dashboard","Every open PO and claim in one view."),
  ],
  cta: commonCta,
};

export const BUYER_PRODUCT = {
  discover,
  "multi-supplier": multiSupplier,
  rfq, reorder,
  "credit-terms":     creditTerms,
  "trade-score":      tradeScore,
  "credit-dashboard": creditDashboard,
  pay,
  "auto-debit":       autoDebit,
  financing,
  "split-pay":        splitPay,
  ship,
  insurance: insuranceBuyer,
};
